// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Client entry / bootstrap.
 *
 * For prerendered public routes the served HTML already contains the page
 * markup, so we hydrate (preserving the painted DOM — and therefore the early
 * LCP). For CSR-only routes (the auth app, served the empty `spa.html` shell)
 * we create a fresh root.
 *
 * DOM-emitting, browser-only widgets (Toaster, PWA prompt, devtools, the legal
 * gate) are wrapped in <ClientOnly> so they render nothing on the first
 * (hydration) pass — keeping the hydrated DOM identical to the trimmed server
 * tree in `entry-server.tsx`. The context-only providers (Auth, Language, Sync)
 * emit no DOM and are safe to include directly.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { TooltipProvider } from '@/components/ui/tooltip';

import { I18nextProvider } from 'react-i18next';
import { router } from '@/router';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AuthProvider } from '@/providers/AuthProvider';
import { LanguageProvider } from '@/providers/LanguageProvider';
import { LayoutTemplateProvider } from '@/providers/LayoutTemplateProvider';
import { PWAUpdatePrompt } from '@/components/pwa';
import { LegalAcceptanceGate } from '@/components/legal/LegalAcceptanceGate';
import { SyncWorkerProvider } from '@/sync/SyncWorkerProvider';
import { ClientOnly } from '@/components/ClientOnly';
import { initializeLogger } from '@/lib/logger';
import { setupGlobalErrorHandlers } from '@/lib/logger/globalHandlers';
import { setupNetworkLogging } from '@/lib/logger/networkLogger';
import { setupDevToolsCommands } from '@/lib/logger/devtools';
import { overrideConsole } from '@/lib/logger/globalConsoleOverride';
import { initSentry } from '@/lib/sentry';
import { initGA } from '@/lib/analytics/gtag';
import { queryClient } from '@/lib/query-client';
import { initLanguageSync } from '@/lib/api/language-sync';
import i18n from '@/i18n';
import { native } from '@/native';
import { mountNativeLifecycle } from '@/native/lifecycle';
import './index.css';

const SUPPORTED_PATH_LOCALES = new Set(['ar', 'en', 'fr', 'de', 'es']);
const enableDevtools =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEVTOOLS === "true";

function normalizeLocalizedSpaPath() {
  const [, locale, rest = ''] = window.location.pathname.match(/^\/([a-z]{2})(\/.*)?$/) ?? [];

  if (!locale || !SUPPORTED_PATH_LOCALES.has(locale)) {
    return;
  }

  const normalizedPath = rest || '/';
  localStorage.setItem('i18nextLng', locale);
  void i18n.changeLanguage(locale);

  const direction = locale === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = locale;
  document.documentElement.dir = direction;
  document.documentElement.style.direction = direction;

  window.history.replaceState(
    window.history.state,
    '',
    `${normalizedPath}${window.location.search}${window.location.hash}`,
  );
}

normalizeLocalizedSpaPath();

// Get debug level from environment FIRST
// Log level controlled by VITE_DEBUG_LEVEL env var (ERROR, WARN, INFO, DEBUG, TRACE)
const debugLevel = (import.meta.env.VITE_DEBUG_LEVEL?.toUpperCase() || 'ERROR') as 'ERROR' | 'WARN' | 'INFO' | 'DEBUG' | 'TRACE';

// Override console methods BEFORE any other code logs
// This filters ALL console output (including third-party libraries) by DEBUG_LEVEL
overrideConsole(debugLevel);

// Defer Sentry init to after first paint to keep critical path fast
// IMPORTANT: DO NOT attempt to pass router instance to Sentry - it causes "matchRoutes" TypeError (Sentry #81784453)
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => initSentry());
} else {
  setTimeout(() => initSentry(), 2000);
}

// Initialize structured logging system
const logger = initializeLogger({
  service: 'frontend',
  minLevel: debugLevel,
});

logger.info('Application starting', { function: 'main' });
setupGlobalErrorHandlers();
setupNetworkLogging();
setupDevToolsCommands();
logger.debug('Logging system initialized', { function: 'main' });

// Initialize language sync for Accept-Language header
// This invalidates queries when language changes to refetch with new header
initLanguageSync();
logger.debug('Language sync initialized', { function: 'main' });

// Initialize Google Analytics (no-op unless VITE_GA_MEASUREMENT_ID is set)
initGA();

if (native.isNative) {
  mountNativeLifecycle();
}

// Create helmet context
const helmetContext = {};

const app = (
  <React.StrictMode>
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary>
        <HelmetProvider context={helmetContext}>
          <QueryClientProvider client={queryClient}>
            <SyncWorkerProvider />
            <AuthProvider>
              <LanguageProvider>
                <LayoutTemplateProvider>
                  <TooltipProvider delayDuration={0}>
                    <ClientOnly>
                      <LegalAcceptanceGate />
                    </ClientOnly>
                    <RouterProvider router={router} context={{ queryClient }} />
                  </TooltipProvider>
                </LayoutTemplateProvider>
              </LanguageProvider>
            </AuthProvider>
            <ClientOnly>
              {enableDevtools ? <ReactQueryDevtools initialIsOpen={false} /> : null}
              <Toaster
                position="top-right"
                richColors
                closeButton
                toastOptions={{
                  duration: 4000,
                  classNames: {
                    toast: 'bg-background text-foreground border-border',
                    title: 'text-foreground',
                    description: 'text-muted-foreground',
                  },
                }}
              />
              <PWAUpdatePrompt />
            </ClientOnly>
          </QueryClientProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </I18nextProvider>
  </React.StrictMode>
);

// Render the app. Hydrate when the route was prerendered to static HTML and the
// served document matches the current path; otherwise client-render fresh.
const rootElement = document.getElementById('root')!;
const prerenderedPath = rootElement.getAttribute('data-prerendered-path');
const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';

if (rootElement.firstChild && prerenderedPath === currentPath) {
  ReactDOM.hydrateRoot(rootElement, app);
} else {
  // CSR-only route, or a fallback document whose prerendered content does not
  // match this path — discard any stale markup and mount fresh.
  rootElement.innerHTML = '';
  ReactDOM.createRoot(rootElement).render(app);
}
